import appConfig from '../config/configGetters/configAppLinksGetters';
import * as browserCompatibleSelectedOptionsData from '../consts/analytics/browserCompatibleSelectedOptionsData';
import {makeSelectAggregatedSettings} from '../state/selectors/consumer';
import {selectIotDeviceType} from '../state/selectors/iotDevice';
import {getState} from '../state/store';
import deviceTypesService from './device/deviceTypesService';
import consumerAggregatedSettingsService from './icc/consumerAggregatedSettingsService';

window.dataLayer = [
    {
        environment: process.env.REACT_APP_ENV === 'prod' ? 'production' : 'staging',
        page: {
            attributes: {
                country: '',
            },
        },
    },
];

const pushToDataLayer = (message) => {
    const aggregatedSettings = makeSelectAggregatedSettings()(getState());
    const dataLayer = window.dataLayer;

    if (dataLayer) {
        if (aggregatedSettings) {
            const isGAEnabled = consumerAggregatedSettingsService.isEnabledGA();

            if (isGAEnabled) {
                dataLayer.push(message);
            }
        } else {
            dataLayer.push(message);
        }
    }
};

const getDataLayerObject = ({eventAction, eventNonInteraction = false, eventLabel, ...rest}) => {
    return {
        event: 'FWUWA',
        eventCategory: 'Firmware Update WebApp',
        eventAction,
        eventLabel,
        eventNonInteraction,
        ...rest,
    };
};

const getDataLayerObjectWithDeviceNameAndPlatform = ({deviceType, ...props}) => {
    const iotDeviceType = selectIotDeviceType(getState());
    const type = iotDeviceType || deviceType;
    const deviceModel = deviceTypesService.getDeviceModelByType(type);
    const devicePlatform = deviceTypesService.getDevicePlatform(type);

    return getDataLayerObject({deviceModel, devicePlatform, ...props});
};

const pushServiceNotAvailable = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'This service is not available in your country',
            eventLabel: 'This service is not available in your country',
        })
    );
};

const pushCannotLocateRightNow = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'We cannot locate you right now',
            eventLabel: 'We cannot locate you right now',
        })
    );
};

const pushContactUs = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Contact us',
            eventLabel: 'Contact us',
        })
    );
};

const pushFindStoreClick = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - Find a store',
            eventLabel: 'Contact us - Find a store',
        })
    );
};

const pushImportantPageFindStoreClick = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - Important',
            eventLabel: 'Important - Find a store',
        })
    );
};

const pushLightAgeGate = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Light Age Gate',
            eventLabel: 'Light Age Gate',
        })
    );
};

const pushLightAgeGateLocked = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Light Age Gate',
            eventLabel: 'Light Age Gate - locked',
        })
    );
};

const pushUpdateViaBleClick = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - Important',
            eventLabel: 'Important - continue',
        })
    );
};

const getCompatibleSelectedOption = (link) => {
    switch (link) {
        case appConfig.getLinkAndroidStoreChrome():
            return browserCompatibleSelectedOptionsData.CHROME_MOBILE;
        case appConfig.getLinkIosStoreBluenet():
            return browserCompatibleSelectedOptionsData.BLUEFY;
        case appConfig.getLinkDesktopChrome():
            return browserCompatibleSelectedOptionsData.CHROME_DESKTOP;
        default:
            return link;
    }
};

const pushBrowserCompatibleEvent = (link) => {
    const selectedOption = getCompatibleSelectedOption(link);

    pushToDataLayer(
        getDataLayerObject({
            eventAction: `Click - CTA - browser is not compatible`,
            eventLabel: `selected option - ${selectedOption}`,
        })
    );
};

const pushGoodPracticesContinueClick = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - Good Practices continue',
            eventLabel: 'Good Practices continue',
        })
    );
};

const pushGoodPracticesClick = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - Good Conversion Practices',
            eventLabel: 'Good Practices',
        })
    );
};

const pushTermsOfUseData = (isSuccess) => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - terms of use',
            eventLabel: isSuccess ? 'agree' : 'decline',
        })
    );
};

const pushSomethingWentWrong = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Something went wrong',
            eventLabel: 'Something went wrong',
        })
    );
};

const pushConnectDeviceSelectionStart = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Connection flow - step 1',
            eventLabel: 'connection flow started',
        })
    );
};

const pushConnectRequestUsbHidSystemWindow = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Connection flow - step 2',
            eventLabel: 'request usbhid system window',
        })
    );
};

const pushConnectContinueUsbHidSystemWindow = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Connection flow - step 2',
            eventLabel: 'continue usbhid system window',
        })
    );
};

const pushConnectConnectingDevice = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Connection flow - step 3',
            eventLabel: 'connecting device',
        })
    );
};

const pushConnectSuccessfullyConnected = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connection flow - step 4',
            eventLabel: 'successfully connected',
        })
    );
};

const pushFlowError = (type, errorType) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing/Connect flow - step error',
            deviceType: type,
            eventLabel: errorType,
        })
    );
};

const pushPairingSelectedDeviceClick = (type) => {
    const deviceModel = deviceTypesService.getDeviceModelByType(type);

    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 1',
            eventLabel: `selected device - ${deviceModel}`,
            deviceType: type,
        })
    );
};

const pushPairingStartGuide = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 2',
            eventLabel: 'Pairing Guide',
            deviceType: type,
        })
    );
};

const pushPairingBluetoothEnabledEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 3',
            eventLabel: 'request bluetooth system window',
            deviceType: type,
        })
    );
};

const pushConnectContinueBluetoothEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 3',
            eventLabel: 'continue bluetooth system window',
            deviceType: type,
        })
    );
};

const pushPairingDevicePairingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 4',
            eventLabel: 'pairing device',
            deviceType: type,
        })
    );
};

const pushPairingSuccessfulEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 5',
            eventLabel: 'successfully paired',
            deviceType: type,
        })
    );
};

const pushPairingSkipEvent = (type, current, total) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step quit',
            eventLabel: `quit set up - ${current} | ${total}`,
            deviceType: type,
        })
    );
};

const pushCheckFwu = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'checking fwu',
        })
    );
};

const pushAvailableFwu = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'newer fw is available',
        })
    );
};

const pushStartFwu = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - start fwu',
            eventLabel: 'start fwu',
        })
    );
};

const pushConfirmFwu = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - start fwu',
            eventLabel: 'start fwu - confirm',
        })
    );
};

const pushFwuInProgress = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'fwu in progress',
        })
    );
};

const pushFwuIsInstalled = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'part of fwu is installed',
        })
    );
};

const pushFWUReconnectActionEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'reconnect device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushContinueFwuInstalling = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - Continue fwu',
            eventLabel: 'continue fwu',
        })
    );
};

const pushFwuUpdateIsFailed = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - failed',
            eventLabel: 'fwu in progress',
        })
    );
};

const pushFwuIsUpToDate = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'fwu is up to date',
        })
    );
};

const pushFwuFailed = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - failed',
            eventLabel: 'fwu is completed',
        })
    );
};

const pushFewMoreSteps = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 1',
            eventLabel: 'Select LED',
        })
    );
};

const pushCompletingYourFirmwareUpgrade = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 2',
            eventLabel: 'Special flow for holder DUO - next',
        })
    );
};

const pushSpecialFlowFailed = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 2',
            eventLabel: 'Special flow for holder DUO - holder issue',
        })
    );
};

const pushSpecialFlowGuide = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 3',
            eventLabel: 'Special flow for holder DUO - Guide',
        })
    );
};

const pushFinalizingYourFirmwareUpgrade = () => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 4',
            eventLabel: 'Special flow for holder DUO - finalizing',
        })
    );
};

export default {
    pushAvailableFwu,
    pushBrowserCompatibleEvent,
    pushCannotLocateRightNow,
    pushCheckFwu,
    pushCompletingYourFirmwareUpgrade,
    pushConfirmFwu,
    pushConnectConnectingDevice,
    pushConnectContinueBluetoothEvent,
    pushConnectContinueUsbHidSystemWindow,
    pushConnectDeviceSelectionStart,
    pushConnectRequestUsbHidSystemWindow,
    pushConnectSuccessfullyConnected,
    pushContactUs,
    pushContinueFwuInstalling,
    pushFewMoreSteps,
    pushFinalizingYourFirmwareUpgrade,
    pushFindStoreClick,
    pushFlowError,
    pushFwuFailed,
    pushFwuInProgress,
    pushFwuIsInstalled,
    pushFWUReconnectActionEvent,
    pushFwuIsUpToDate,
    pushFwuUpdateIsFailed,
    pushGoodPracticesClick,
    pushGoodPracticesContinueClick,
    pushImportantPageFindStoreClick,
    pushLightAgeGate,
    pushLightAgeGateLocked,
    pushPairingBluetoothEnabledEvent,
    pushPairingDevicePairingEvent,
    pushPairingSelectedDeviceClick,
    pushPairingSkipEvent,
    pushPairingStartGuide,
    pushPairingSuccessfulEvent,
    pushServiceNotAvailable,
    pushSomethingWentWrong,
    pushSpecialFlowFailed,
    pushSpecialFlowGuide,
    pushStartFwu,
    pushTermsOfUseData,
    pushUpdateViaBleClick,
};
